<template>
    <AppLayout :loading="processing">
        <div class="max-w-7xl mx-auto px-4 py-6">
            <!-- Page Title -->
            <div class="mb-6">
                <h1 class="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                    {{ $tc('LBL_USER', 2) }}
                </h1>
                <!-- Breadcrumb -->
                <nav
                    class="text-sm text-gray-500 mb-6"
                    aria-label="Breadcrumb"
                >
                    <ol class="flex items-center space-x-2">
                        <li class="flex items-center">
                            <inertia-link
                                :href="route('landing')"
                                class="text-green-600 hover:underline"
                            >
                                {{ $t('LBL_HOME') }}
                            </inertia-link>
                            <svg
                                class="w-3 h-3 mx-2 text-green-400"
                                fill="currentColor"
                                viewBox="0 0 320 512"
                            >
                                <path
                                    d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"
                                />
                            </svg>
                        </li>
                        <li class="text-gray-700">
                            {{ $tc('LBL_USER', 1) }}
                        </li>
                    </ol>
                </nav>
            </div>
            <!-- breadcrumb end -->

            <div
                class="w-full bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-visible"
            >
                <!-- Search & Action Bar -->
                <div class="flex flex-wrap items-center justify-between gap-4 p-4 sm:flex-nowrap">
                    <!-- Search Box -->
                    <div class="relative w-full sm:w-auto">
                        <label
                            for="table-search"
                            class="sr-only"
                            >Search</label
                        >
                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg
                                class="w-5 h-5 text-gray-500 dark:text-gray-400"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </div>
                        <t-input
                            id="user-search-box"
                            v-model="search"
                            variant="success"
                            class="w-full sm:w-80 pl-10 pr-4 py-2.5 text-sm rounded-md border border-gray-300 focus:ring-green-500 focus:outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            placeholder="Search for user"
                            @keypress.enter="fetchPageData()"
                        />
                    </div>

                    <!-- Action Buttons -->
                    <div class="flex flex-wrap gap-2">
                        <t-button
                            v-if="isSuperAdmin"
                            variant="success"
                            @click="toggleRightSidebar('admin')"
                        >
                            Add Admin
                        </t-button>
                        <t-button
                            v-if="isWholesaler"
                            variant="success"
                            @click="toggleRightSidebar('salesperson')"
                        >
                            Add Sales Person
                        </t-button>
                        <t-button
                            v-if="isSuperAdmin"
                            variant="success"
                            @click="goto('kyc.create')"
                        >
                            Add Wholesaler
                        </t-button>
                    </div>
                </div>

                <div
                    class="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-none shadow-md"
                >
                    <div class="overflow-x-auto">
                        <table
                            class="min-w-[700px] w-full table-auto text-sm sm:text-base text-left text-gray-700 dark:text-gray-300"
                        >
                            <thead class="bg-gray-100 dark:bg-gray-700 text-sm font-semibold">
                                <tr>
                                    <th class="px-4 py-3">#</th>
                                    <th class="px-4 py-3">NAME</th>
                                    <th class="px-4 py-3">EMAIL</th>
                                    <th class="px-4 py-3">ROLE</th>
                                    <th class="px-4 py-3">CREATED</th>
                                    <th class="px-4 py-3">STATUS</th>
                                    <th class="px-4 py-3">ACTIONS</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="(row, rowIndex) in users.data">
                                    <tr
                                        :class="[
                                            'border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition duration-200',
                                            row.id === $page.props.user.id ? 'bg-green-100 hover:bg-green-100' : '',
                                        ]"
                                    >
                                        <td class="px-4 py-3">{{ rowIndex + 1 + (currentPage - 1) * perPage }}</td>
                                        <td class="px-4 py-3">{{ row.name }}</td>
                                        <td class="px-4 py-3">{{ row.email }}</td>
                                        <td class="px-4 py-3">{{ row.roles[0].name }}</td>
                                        <td class="px-4 py-3">{{ row.created_at | dateLabel }}</td>
                                        <td class="px-4 py-3">{{ row.deleted_at ? 'Suspended' : 'Active' }}</td>
                                        <td class="px-4 py-3 relative">
                                            <div class="inline-flex shadow-sm rounded-md overflow-visible">
                                                <!-- Primary Action -->
                                                <div v-if="row.id === $page.props.user.id">
                                                    <span
                                                        class="rounded-md border border-green-300 bg-green-50 overflow-hidden text-green-600 text-sm px-6 py-2"
                                                    >
                                                        Self
                                                    </span>
                                                </div>

                                                <div
                                                    v-else-if="
                                                        isSuperAdmin &&
                                                        !!row.roles.find(
                                                            (role) =>
                                                                role.name === 'superadmin' || role.name === 'admin',
                                                        )
                                                    "
                                                >
                                                    <t-button
                                                        variant="group"
                                                        size="sm"
                                                        class="rounded-none rounded-l-md"
                                                        :class="
                                                            row.deleted_at ||
                                                            (row.roles.find((role) => role.name !== 'salesperson') &&
                                                                'rounded-r-md')
                                                        "
                                                        @click="toggleRole(row)"
                                                    >
                                                        Change Role
                                                    </t-button>
                                                </div>

                                                <div v-else-if="!row.deleted_at">
                                                    <t-button
                                                        variant="group"
                                                        class="rounded-none rounded-l-md"
                                                        :class="
                                                            row.deleted_at ||
                                                            (row.roles.find((role) => role.name !== 'salesperson') &&
                                                                'rounded-r-md')
                                                        "
                                                        size="sm"
                                                        @click="suspendUser(row)"
                                                    >
                                                        Suspend
                                                    </t-button>
                                                </div>

                                                <div v-else>
                                                    <t-button
                                                        variant="error"
                                                        size="sm"
                                                        class="rounded-none rounded-l-md"
                                                        :class="
                                                            row.deleted_at ||
                                                            (row.roles.find((role) => role.name !== 'salesperson') &&
                                                                'rounded-r-md')
                                                        "
                                                        @click="unsuspendUser(row)"
                                                    >
                                                        Activate
                                                    </t-button>
                                                </div>

                                                <!-- Dropdown Toggle Button -->
                                                <!-- <div
                                                    v-if="
                                                        !row.deleted_at &&
                                                        row.roles.find((role) => role.name === 'salesperson')
                                                    "
                                                    class="relative dropdown-wrapper"
                                                    :ref="'dropdown-container-' + row.id"
                                                >
                                                    <t-button
                                                        variant="groupoutline"
                                                        size="sm"
                                                        class="border rounded-none rounded-r-md py-2 w-10"
                                                        @click.stop="toggleDropdown(row.id)"
                                                    >
                                                        . . .
                                                    </t-button> -->

                                                <!-- Dropdown Trigger -->
                                                <t-button
                                                    v-if="
                                                        row.roles.find((role) => role.name === 'salesperson') &&
                                                        !row.deleted_at
                                                    "
                                                    variant="groupoutline"
                                                    size="sm"
                                                    class="border rounded-r-md py-2 w-10"
                                                    @click.stop="toggleDropdown(row.id, $event)"
                                                >
                                                    . . .
                                                </t-button>

                                                <!-- Dropdown Menu -->
                                                <!-- Portal for Dropdown -->
                                                <portal
                                                    to="dropdown-target"
                                                    v-if="activeDropdownId === row.id"
                                                >
                                                    <div
                                                        class="absolute z-50 w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg"
                                                        :style="dropdownPosition"
                                                    >
                                                        <ul class="text-sm text-gray-700 dark:text-gray-200">
                                                            <li v-if="isWholesaler">
                                                                <t-button
                                                                    @click="showAssignRetailersModal(row)"
                                                                    variant="outline"
                                                                    class="block w-full px-4 py-2 border-none hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
                                                                >
                                                                    Assign Retailers
                                                                </t-button>
                                                            </li>
                                                            <li>
                                                                <t-button
                                                                    @click="showSalesPersonDetail(row)"
                                                                    variant="outline"
                                                                    class="block w-full px-4 py-2 border-none hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
                                                                >
                                                                    View Report
                                                                </t-button>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </portal>
                                                <!-- </div> -->
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                                <tr
                                    v-if="!users.data.length"
                                    class="text-center"
                                >
                                    <td
                                        colspan="7"
                                        class="px-4 py-4 text-gray-500 dark:text-gray-400"
                                    >
                                        {{ $t('LBL_DATA_FOUND') }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div
                        v-if="users.data.length"
                        class="flex justify-center items-center bg-gray-100 dark:bg-gray-800 px-4 py-4 border-t dark:border-gray-700"
                    >
                        <t-pagination
                            v-model="currentPage"
                            variant="rounded"
                            :total-items="totalRows"
                            :per-page="perPage"
                            :limit="limit"
                            :disabled="!users.data.length"
                            @change="fetchPageData"
                        />
                    </div>
                    <p class="sm:hidden text-xs text-center text-gray-500 py-2">
                        ↔ Swipe left/right to scroll the table
                    </p>
                </div>
            </div>

            <!-- <t-modal v-model="retailerAssignModal" header="Assign Retailers to Salesperson">
            <div>
                <div v-if="currentSalesperson">
                    Salesperson: {{ currentSalesperson.name }}
                </div>
                <div>
                    <Multiselect v-model="selectedRetailers" :options="selectableRetailersForCurrentSalesPerson()"
                        :multiple="true" :close-on-select="false" placeholder="Pick Retailers" label="name"
                        track-by="name" />
                </div>
            </div>
            <template #footer>
                <div class="flex justify-between">
                    <t-button variant="outline" @click="hideAssignRetailersModal">
                        Cancel
                    </t-button>
                    <t-button variant="success" @click="assignRetailers">
                        Assign
                    </t-button>
                </div>
            </template>
        </t-modal> -->

            <!-- Assign Retailer Dailog Box -->
            <t-modal
                v-model="retailerAssignModal"
                header="Assign Retailers to Salesperson"
            >
                <div class="space-y-6 px-2 py-4 text-md sm:text-base text-gray-800 dark:text-gray-200">
                    <div
                        v-if="errorMessage"
                        class="bg-red-100 text-red-700 px-4 py-2 rounded mb-4"
                    >
                        {{ errorMessage }}
                    </div>
                    <div class="text-md font-semibold text-gray-700 dark:text-gray-100">
                        <span class="block">Salesperson:</span>
                        <span class="text-green-600 dark:text-green-400">{{ currentSalesperson?.name }}</span>
                    </div>

                    <div>
                        <label class="block mb-2 font-medium text-gray-600 dark:text-gray-300">Select Retailers</label>
                        <Multiselect
                            v-model="selectedRetailers"
                            :options="selectableRetailersForCurrentSalesPerson()"
                            :multiple="true"
                            :close-on-select="false"
                            placeholder="Pick Retailers"
                            label="name"
                            track-by="name"
                            class="w-full multiselect-custom"
                        />
                    </div>
                </div>

                <template #footer>
                    <div class="flex justify-end gap-3 border-t pt-4 px-6">
                        <t-button
                            variant="outline"
                            class="px-4 py-2"
                            @click="hideAssignRetailersModal"
                        >
                            Cancel
                        </t-button>
                        <t-button
                            variant="success"
                            class="px-4 py-2 font-medium"
                            @click="assignRetailers"
                        >
                            Assign
                        </t-button>
                    </div>
                </template>
            </t-modal>

            <RightSidebar
                :is-open="rightSidebar"
                :component-name="rightSidebarComponentName"
                @closeRightSidebar="toggleRightSidebar"
                @set-processing="processing = $event"
            >
                <template #heading>
                    <div class="relative mt-4">
                        <div class="text-xl font-bold absolute left-0">
                            {{ sidebarTitle }}
                        </div>
                        <div
                            class="text-md text-right cursor-pointer absolute right-0"
                            @click="rightSidebar = false"
                        >
                            close
                        </div>
                    </div>
                </template>
            </RightSidebar>
            <portal-target
                name="dropdown-target"
                multiple
            ></portal-target>
        </div>
    </AppLayout>
</template>

<script>
    import _ from 'lodash';
    import Multiselect from 'vue-multiselect';
    import AppLayout from '@/Layouts/AppLayout.vue';
    import RightSidebar from '@/Components/RightSidebar.vue';

    export default {
        name: 'UserManagement',

        components: {
            AppLayout,
            RightSidebar,
            Multiselect,
        },

        props: {
            users: {
                type: Object,
                required: true,
            },
            selectableRetailers: {
                type: Array,
                required: true,
            },
            appliedFilters: {
                type: Object,
                required: true,
            },
        },

        data() {
            return {
                sidebarTitle: '',
                processing: false,
                retailerAssignModal: false,
                currentSalesperson: null,
                selectedRetailers: [],
                rightSidebar: false,
                rightSidebarComponentName: 'CreateAdmin',
                currentPage: null,
                limit: 5,
                perPage: this.users.per_page,
                totalRows: this.users.total,
                search: null,
                mounted: false,
                disabled: false,
                activeDropdownId: null,
                dropdownPosition: {
                    top: '0px',
                    right: '0px',
                },
                errorMessage: '',
            };
        },

        watch: {
            search() {
                if (this.mounted) {
                    this.debouncedFetchPageData();
                }
            },
        },

        created() {
            this.currentPage = this.users.current_page;
        },

        beforeDestroy() {
            document.removeEventListener('click', this.handleClickOutside);
        },

        mounted() {
            this.disabled = this.users.data.length === 0;

            if (this.appliedFilters.keyword) {
                this.search = this.appliedFilters.keyword;
            }

            this.debouncedFetchPageData = _.debounce(this.fetchPageData, 2000);

            // ✅ Close dropdown on outside click
            document.addEventListener('click', this.handleClickOutside);

            this.$nextTick(() => {
                this.mounted = true;
            });
        },

        methods: {
            // toggleDropdown(id) {
            //     this.activeDropdownId = this.activeDropdownId === id ? null : id;
            // },

            toggleDropdown(userId, event) {
                if (this.activeDropdownId === userId) {
                    this.activeDropdownId = null;
                    return;
                }

                const trigger = event.currentTarget.getBoundingClientRect();
                const scrollY = window.scrollY || window.pageYOffset;
                const scrollX = window.scrollX || window.pageXOffset;

                // Assume dropdown width is 176px (w-44) = 11rem
                const dropdownWidth = 176;
                this.dropdownPosition = {
                    top: `${trigger.bottom + scrollY}px`,
                    left: `${trigger.right + scrollX - dropdownWidth}px`,
                };

                this.activeDropdownId = userId;
            },

            handleClickOutside(event) {
                // Close dropdown if the click is outside any open dropdown
                if (!event.target.closest('.dropdown-wrapper')) {
                    this.activeDropdownId = null;
                }
            },

            // used in dropdown to select retailer
            selectableRetailersForCurrentSalesPerson() {
                if (!this.currentSalesperson) return [];

                return this.selectableRetailers.filter((retailer) => {
                    // Allow retailers that are:
                    // - Not taken by any salesperson
                    // - Or taken by the current salesperson
                    return (
                        !retailer.taken_by_salesperson ||
                        retailer.taken_by_salesperson_id === this.currentSalesperson.id
                    );
                });

                // if (this.currentSalesperson) {
                // return this.selectableRetailers.filter(
                //     (retailer) =>
                //         !retailer.inverse_associated_users
                //             .map((associatedUsers) => associatedUsers.id)
                //             .includes(this.currentSalesperson.id),
                // );
                // }
                // return [];
            },

            // add the already assigned retailer to the selected salesperson
            showAssignRetailersModal(user) {
                this.currentSalesperson = user;
                this.retailerAssignModal = true;

                // Preselect retailers already associated with this salesperson
                this.selectedRetailers = this.selectableRetailers.filter((retailer) =>
                    retailer.inverse_associated_users.some((associatedUser) => associatedUser.id === user.id),
                );
            },

            hideAssignRetailersModal() {
                this.selectedRetailers = [];
                this.currentSalesperson = null;
                this.retailerAssignModal = false;
            },

            assignRetailers() {
                const retailerIdArray = this.selectedRetailers.map((retailer) => retailer.id);
                this.$inertia.post(
                    route('users.management.salesperson.assign', this.currentSalesperson.id),
                    { retailerIds: retailerIdArray },
                    {
                        onSuccess: () => {
                            this.selectedRetailers = [];
                            this.currentSalesperson = null;
                            this.retailerAssignModal = false;
                        },
                    },
                );
            },

            toggleRole(user) {
                let newRole = null;

                if (user.roles.find((role) => role.name === 'admin')) {
                    newRole = 'superadmin';
                } else if (user.roles.find((role) => role.name === 'superadmin')) {
                    newRole = 'admin';
                }
                this.$dialog
                    .confirm({
                        title: 'Confirm',
                        text: `Are you sure you want to change role of this user to ${newRole}?`,
                    })
                    .then((result) => {
                        if (result.isOk) {
                            this.$inertia.post(route('users.management.admin.toggleRole', user.id));
                        }
                    });
            },

            suspendUser(user) {
                this.$dialog
                    .confirm({
                        title: 'Confirm',
                        text: 'Are you sure you want to suspend this user?',
                    })
                    .then((result) => {
                        if (result.isOk) {
                            this.$inertia.delete(route('users.management.admin.suspend', user.id));
                        }
                    });
            },

            unsuspendUser(user) {
                this.$dialog
                    .confirm({
                        title: 'Confirm',
                        text: 'Are you sure you want to activate this user?',
                    })
                    .then((result) => {
                        if (result.isOk) {
                            this.$inertia.post(route('users.management.admin.unsuspend', user.id));
                        }
                    });
            },

            showSalesPersonDetail(user) {
                console.log(user);
                this.$inertia.get(
                    route('reports.salesperson.orders', {
                        salesperson: user.id,
                    }),
                );
            },

            toggleRightSidebar(forUserType = null) {
                this.rightSidebar = !this.rightSidebar;
                if (this.rightSidebar) {
                    if (forUserType === 'admin') {
                        this.rightSidebarComponentName = 'CreateAdmin';
                        this.sidebarTitle = 'Create Admin';
                    } else {
                        this.rightSidebarComponentName = 'CreateSalesperson';
                        this.sidebarTitle = 'Create Salesperson';
                    }
                    // forUserType === 'admin' ? this.rightSidebarComponentName = 'CreateAdmin' : this.rightSidebarComponentName = 'CreateSalesperson';
                }
            },

            fetchPageData(newPage) {
                this.$inertia.get(
                    route('users.management.index', {
                        page: newPage,
                        keyword: this.search,
                    }),
                );
            },
        },
    };
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style>
    .multiselect-custom .multiselect__tags {
        @apply border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-sm;
    }

    .multiselect-custom .multiselect__tag {
        @apply bg-blue-100 text-blue-800 rounded-full px-2 py-1.5 text-xs mr-1 mb-1 flex items-center;
    }

    .multiselect-custom .multiselect__option--highlight {
        @apply bg-green-500 text-white;
    }

    .multiselect-custom .multiselect__option--selected.multiselect__option--highlight {
    }

    .multiselect-custom .multiselect__input {
        @apply bg-transparent text-sm py-2 focus:ring-green-500 focus:outline-none text-gray-700 dark:text-gray-100;
    }

    .multiselect-custom .multiselect__placeholder {
        @apply text-gray-500 dark:text-gray-400;
    }
</style>
